import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import SuiBox from "components/SuiBox";
import Box from "@mui/material/Box";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";
import { Divider } from "@mui/material";
import ProcesosApi from "api/procesosApi";
import BienCard from "./components/BienCard";
import EditBienModal from "./components/EditBienModal";
import NuevoBienForm from "./components/NuevoBienForm";

const TIPOS_BIENES = ["INMUEBLE", "MUEBLE"];

const BienesManager = ({
  procesoId,
  showNewBienModal,
  setShowNewBienModal,
  handleNext = () => {},
}) => {
  const [bienes, setBienes] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBien, setSelectedBien] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [procesoData, setProcesoData] = useState(null);

  const verificarProceso = async () => {
    try {
      setLoading(true);
      setError(null);
      // Obtener los datos de la proceso
      const procesoResponse = await ProcesosApi.fetchProcesoById(procesoId);
      const procesoActual = procesoResponse.data;
      if (!procesoActual) {
        setError("No se encontró el Proceso");
        return;
      }
      setProcesoData(procesoActual);
      if (!procesoActual.posee_bienes) {
        if (handleNext) {
          handleNext();
        }
        return;
      }
      // Si posee bienes, cargarlos
      const bienesResponse = await ProcesosApi.obtenerBienesProceso(procesoId);
      setBienes(bienesResponse.data?.bienes || []);
    } catch (error) {
      console.error("Error al cargar los datos de los Bienes del Proceso:", error);
      setError(obtenerMensajeError(error, "Error al cargar los datos de los Bienes del Proceso"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (procesoId) {
      verificarProceso();
    }
  }, [procesoId]);

  const handleDelete = async (bien) => {
    if (!window.confirm("¿Está seguro(a) de eliminar este bien?")) return;

    try {
      setError(null);
      await ProcesosApi.eliminarBien(procesoId, bien.id);
      // Recargar bienes después de eliminar
      const response = await ProcesosApi.obtenerBienesProceso(procesoId);
      setBienes(response.data?.bienes || []);
    } catch (error) {
      console.error("Error al eliminar bien:", error);
      setError(obtenerMensajeError(error, "Error al eliminar el bien"));
    }
  };

  const handleEdit = (bien) => {
    setSelectedBien(bien);
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (bienActualizado) => {
    try {
      setError(null);
      const response = await ProcesosApi.actualizarBien(
        procesoId,
        bienActualizado.id,
        bienActualizado
      );

      // Actualizar la lista de bienes con el bien actualizado
      setBienes((prevBienes) =>
        prevBienes.map((bien) => (bien.id === bienActualizado.id ? response.data : bien))
      );

      setEditModalOpen(false);
      setSelectedBien(null);
    } catch (error) {
      console.error("Error al actualizar bien:", error);
      setError(obtenerMensajeError(error, "Error al actualizar el bien"));
    }
  };

  const handleNewBienSubmit = async (bienData) => {
    try {
      setError(null);
      const response = await ProcesosApi.crearBien(procesoId, bienData);
      setBienes((prevBienes) => [...prevBienes, response.data]);
      setShowNewBienModal(false);
    } catch (error) {
      console.error("Error al crear Bien:", error);
      setError(obtenerMensajeError(error, "Error al crear el Bien"));
    }
  };

  const bienesPorTipo = useMemo(() => {
    return bienes.reduce((acc, bien) => {
      const tipo = bien.tipo_bien;
      if (!acc[tipo]) acc[tipo] = [];
      acc[tipo].push(bien);
      return acc;
    }, {});
  }, [bienes]);

  // Si está cargando, mostrar indicador
  if (loading) {
    return (
      <SuiBox textAlign="center" py={3}>
        <SuiTypography variant="button" fontWeight="regular">
          Verificando información de bienes...
        </SuiTypography>
      </SuiBox>
    );
  }

  // Si hay error, mostrar mensaje
  if (error) {
    return (
      <SuiBox textAlign="center" py={3}>
        <SuiTypography variant="button" color="error" fontWeight="regular">
          {error}
        </SuiTypography>
      </SuiBox>
    );
  }

  // Si no se encontró la proceso o no posee bienes, no mostrar nada
  if (!procesoData || !procesoData.posee_bienes) {
    return (
      <SuiBox p={2}>
        <SuiBox textAlign="center" py={3}>
          <SuiTypography variant="button" color="text" fontWeight="regular">
            El Proceso no posee bienes registrados.
          </SuiTypography>
        </SuiBox>
      </SuiBox>
    );
  }

  return (
    <SuiBox p={2}>
      <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <SuiTypography variant="h6">Bienes Registrados</SuiTypography>
        <SuiButton variant="gradient" buttonColor="info" onClick={() => setShowNewBienModal(true)}>
          Agregar Bien
        </SuiButton>
      </SuiBox>

      {bienes.length === 0 ? (
        <SuiBox textAlign="center" py={3}>
          <SuiTypography variant="button" color="text" fontWeight="regular">
            No hay bienes registrados. Haga clic en &quot;Agregar Bien&quot; para comenzar.
          </SuiTypography>
        </SuiBox>
      ) : (
        TIPOS_BIENES.map((tipo) => {
          const bienesTipo = bienesPorTipo[tipo] || [];
          if (bienesTipo.length === 0) return null;

          return (
            <SuiBox key={tipo} pb={1}>
              <SuiBox pb={2}>
                <Divider
                  sx={{
                    opacity: 0.8,
                  }}
                >
                  <SuiTypography
                    variant="button"
                    fontWeight="bold"
                    textTransform="uppercase"
                    textColor="info"
                  >
                    {tipo === "INMUEBLE" ? "BIENES INMUEBLES" : "BIENES MUEBLES"}
                  </SuiTypography>
                </Divider>
              </SuiBox>
              <Box
                sx={{
                  overflowX: "auto",
                  pb: 2,
                  "&::-webkit-scrollbar": {
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888",
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor: "#555",
                    },
                  },
                }}
              >
                <Box sx={{ display: "flex", gap: 3, minWidth: "min-content" }}>
                  {bienesTipo.map((bien) => (
                    <Box key={bien.id} sx={{ minWidth: "350px" }}>
                      <BienCard bien={bien} onEdit={handleEdit} onDelete={handleDelete} />
                    </Box>
                  ))}
                </Box>
              </Box>
            </SuiBox>
          );
        })
      )}

      {selectedBien && (
        <EditBienModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedBien(null);
          }}
          onSubmit={handleEditSubmit}
          bien={selectedBien}
        />
      )}

      <NuevoBienForm
        open={showNewBienModal}
        onClose={() => setShowNewBienModal(false)}
        onSubmit={handleNewBienSubmit}
        procesoId={procesoId}
      />
    </SuiBox>
  );
};

BienesManager.propTypes = {
  procesoId: PropTypes.number.isRequired,
  showNewBienModal: PropTypes.bool.isRequired,
  setShowNewBienModal: PropTypes.func.isRequired,
  handleNext: PropTypes.func,
};

export default BienesManager;
